import React from "react"
import { PageProps } from "gatsby"
import { useIntl } from "gatsby-plugin-intl"
import classNames from "classnames"
import Layout from "../../../components/Layout/Layout"
import SEO from "../../../components/seo"
import styles from "./geopop.module.scss"
import SecondTitle from "../../../components/UI/SecondTitle/SecondTitle"

const objectiveItems = [
	"SMBs to launch POI-based targeted advertising on their own, or with the help of managers;",
	"Generate accurate custom audiences;",
	"Track campaign performance in real time;",
	"Compare results with competitors on a local level.",
]

const competitorData = [
	{
		title: "The 1st competitor",
		text:
			"Is a powerful platform for analyzing offline user behavior and building audiences based on geo-data. However, the solution requires a high degree of training and does not include direct integration with Meta to run one-click ads.",
	},
	{
		title: "The 2nd competitor",
		text:
			"Offers advanced geolocation targeting and campaign management, but is primarily focused on large businesses and agencies. The interface and cost make it inaccessible to small businesses in India.",
	},
]

const solutionModules = [
	{
		title: "Point of Interest (POI) Builder:",
		items: [
			"Add and group POIs manually or via CSV/map import;",
			"Visualization on the map, working with microgeography - from entire neighborhoods to individual streets.",
		],
	},
	{
		title: "Creating and launching advertising campaigns:",
		items: [
			"Deep integration with Meta Ads API;",
			"Setting up goals, budgets, creatives;",
			"Formation of audiences by behavior in POIs.",
		],
	},
	{
		title: "Analytics and competitive comparison:",
		items: [
			"Dashboard with campaign metrics: reach, engagement, clicks, CPA, etc;",
			"Module of comparison with competitors on similar POIs (if data is available);",
			"Ability to quickly adjust campaigns based on analytics.",
		],
	},
]

const uikitFeatures = [
	"The component structure allows new features and interfaces to be added quickly",
	"Visual style adapted to the local market: readable typography, high information density, optimal color palette",
]

const Geopop: React.FC<PageProps> = ({ location }) => {
	const intl = useIntl()

	return (
		<Layout location={location}>

			<SEO
				language={intl.locale}
				description={intl.formatMessage({ id: "projects-new.list.geopop.description" })}
				title={intl.formatMessage({ id: "projects-new.list.geopop.metatitle" })}
				pathname={location.pathname}
			/>

			{/* Banner Section */}
			<article>
				<SecondTitle desktopImage="/images/pages-heading/header-geopop.jpg" title={`Geopop: \nThe advertising platform `} position="center center" back="white" />
				<div className={styles.geopopPageRoot}>
					{/* About Project Section */}
					<section className={styles.aboutSection}>
						<div className={styles.aboutSectionContainer}>
							<div className={styles.aboutSectionTextBlock}>
								<p className={styles.aboutSectionText}>
									Geopop, an Indian geo-analytics startup, has initiated the
									development of a platform that makes digital marketing simple
									and effective. The solution allows businesses to run ads on Meta
									(Facebook, Instagram) based on customized points of interest
									(POIs) - popular or strategically important locations. Built-in
									analytics and competitor comparison tools help make data-driven
									decisions.
								</p>
							</div>
							<div className={styles.aboutSectionInfo}>
								<div className={styles.aboutSectionInfoRow}>
									<span className={styles.aboutSectionInfoLabel}>Client</span>
									<span className={styles.aboutSectionInfoValue}>
										Geopop (International)
									</span>
								</div>
								<div className={styles.aboutSectionInfoRow}>
									<span className={styles.aboutSectionInfoLabel}>Expertise</span>
									<span className={styles.aboutSectionInfoValue}>
										Full-cycle development
										<br />
										Design support
										<br />
										Analytics
									</span>
								</div>
								<div className={styles.aboutSectionInfoRow}>
									<span className={styles.aboutSectionInfoLabel}>Date</span>
									<span className={styles.aboutSectionInfoValue}>2023-2024</span>
								</div>
							</div>
						</div>
						<img className={`${styles.aboutSectionImage} ${styles.sectionImage}`} src="/images/geopop/geopop-screens.png" alt="Geopop platform screens" />
					</section>

					{/* Objective Section */}
					<section className={styles.objectiveSection}>
						<div className={styles.objectiveSectionTitleBlock}>
							<h2>Objective</h2>
							<div className={styles.objectiveSectionSubtitle}>
								Create a user-friendly and accessible platform that will allow:
							</div>
						</div>
						<ul className={styles.objectiveSectionList}>
							{objectiveItems.map((text, index) => (
								<li key={index} className={styles.objectiveSectionItem}>
									<div className={styles.objectiveSectionItemNumber}>{`0${index + 1
										}`}</div>
									<div className={styles.objectiveSectionItemText}>{text}</div>
								</li>
							))}
						</ul>
					</section>

					{/* Competitive Analysis Section */}
					<section className={styles.competitiveSection}>
						<div className={styles.competitiveSectionTitleBlock}>
							<h2>Competitive analysis</h2>
							<div className={styles.competitiveSectionSubtitle}>
								We analyzed a number of existing solutions in the field of voice and text assistants for retail - from classic chatbots to digital kiosks and AI-based embedded systems.
							</div>
						</div>
						<div className={styles.competitorsBlock}>
							<div className={styles.competitorsBlockCards}>
								{competitorData.map((competitor, index) => (
									<div key={index} className={styles.competitorsBlockCard}>
										<div className={styles.competitorsBlockCardTitle}>{competitor.title}</div>
										<div className={styles.competitorsBlockCardText}>{competitor.text}</div>
									</div>
								))}
							</div>
						</div>
						<div className={styles.competitiveConclusionBlock}>
							<h3>Conclusion</h3>
							<div className={styles.competitiveConclusionBlockText}>
								Existing platforms are focused on the enterprising segment, are difficult to master and do not address the need for a simple, accessible and localized tool for MSMEs. This confirmed the market niche for the Geopop product - a platform that combines a geolocation approach with direct management of advertising in Meta, aimed at small and medium-sized businesses.
							</div>
						</div>
					</section>

					{/* Solution Section */}
					<section className={styles.solutionSection}>
						<h2>Solution</h2>
						<div className={styles.solutionSectionText}>
							We developed a desktop platform with an intuitive interface and three key modules.
						</div>
						<img className={`${styles.sectionImage}`} src="/images/geopop/geopop-steps.png" alt="Geopop steps" />
						<div className={styles.solutionSectionModules}>
							{solutionModules.map((module, index) => (
								<div key={index}>
									<h3>{module.title}</h3>
									<ul className={styles.cardList}>
										{module.items.map((item, itemIndex) => (
											<li key={itemIndex} className={styles.cardListItem}>
												{item}
											</li>
										))}
									</ul>
									{index === 0 && <img className={`${styles.sectionImage}`} src="/images/geopop/geopop-poi.png" alt="Geopop POI" />}
									{index === 1 && <img className={`${styles.sectionImage}`} src="/images/geopop/geopop-audience.png" alt="Geopop audience" />}
								</div>
							))}
						</div>
					</section>

					{/* Geopop GPT Section */}
					<section className={styles.gptSection}>
						<div className={styles.gptTitleBlock}>
							<h2>Geopop GPT - AI-assistant inside the platform</h2>
							<div className={styles.gptSectionSubtitle}>
								To make working with analytics even easier for users without marketing experience.
							</div>
						</div>
						<div className={styles.gptSectionFeaturesBlock}>
							<div className={styles.gptSectionFeaturesTitle}>Key features:</div>
							<div className={classNames(styles.gptSectionFeaturesList, styles.cardList)}>
								<div className={classNames(styles.gptSectionFeature, styles.cardListItem)}>
									<div className={styles.gptSectionFeatureTitle}>Deciphering campaign metrics</div>
									<div className={styles.gptSectionFeatureText}>
										Explains metrics values (e.g. CPC, CTR, CPM) in simple words, without professional jargon;
									</div>
								</div>
								<div className={classNames(styles.gptSectionFeature, styles.cardListItem)}>
									<div className={styles.gptSectionFeatureTitle}>Reasons for deviations in results</div>
									<div className={styles.gptSectionFeatureText}>
										Automatically indicates why one campaign performs better than another (e.g., audience selection, display time, creative);
									</div>
								</div>
								<div className={classNames(styles.gptSectionFeature, styles.cardListItem)}>
									<div className={styles.gptSectionFeatureTitle}>Recommendations for improvement</div>
									<div className={styles.gptSectionFeatureTextWork}>
										Generates specific advice: when to relaunch the campaign, which locations should be tested, which creatives should be changed.
									</div>
								</div>
							</div>
						</div>
						<img className={`${styles.sectionImage}`} src="/images/geopop/geopop-gpt.png" alt="Geopop GPT image" />
						<div className={styles.adaptiveTextBlock}>
							To make working with analytics even easier for users without marketing experience, we have implemented Geopop GPT - an embedded AI-assistant based on LLM (Large Language Model).
						</div>
					</section>

					{/* UI Kit Section */}
					<section className={classNames(styles.uikit)}>
						<h2>
							UI Kit
						</h2>
						<p className={styles.uikitText}>
							To speed up the development and scalability of the interface, a custom UI Kit was created, adapted to the platform's tasks and the peculiarities of the Indian market.
						</p>
						<ul className={classNames(styles.uikitFeatures, styles.cardList)}>
							{uikitFeatures.map((feature, index) => (
								<li key={index} className={classNames(styles.uikitFeatureCard, styles.cardListItem)}>
									{feature}
								</li>
							))}
						</ul>
						<img className={`${styles.uikitImage}`} src="/images/geopop/geopop-uikit.png" alt="Geopop UI Kit" />
					</section>

					{/* Result Section */}
					<section className={styles.resultSection}>
						<h2>Result</h2>
						<div className={classNames(styles.adaptiveTextBlock, styles.resultSectionSubtitle)}>
							Launched Ad Tech, an intuitive platform that helps companies create, launch and monitor multiple ad campaigns and reach their ideal audience with precision.
						</div>
						<div className={classNames(styles.cardList)}>
							<div className={classNames(styles.resultSectionCard, styles.cardListItem)}>
								<div className={`${styles.resultSectionImage}`} />
								<div className={styles.resultSectionCardText}>
									The platform was successfully launched and attracted its first clients.
								</div>
							</div>
							<div className={classNames(styles.resultSectionCard, styles.cardListItem)}>
								<div className={styles.resultSectionCardValue}>+37%</div>
								<div className={styles.resultSectionCardText}>
									Clients from the MSME-segment increased the effectiveness of advertising campaigns.
								</div>
							</div>
							<div className={classNames(styles.cardListItem, styles.resultSectionCard)}>
								<div className={styles.resultSectionCardValue}>+25%</div>
								<div className={styles.resultSectionCardText}>
									Decrease in the cost of customer acquisition compared to manual settings in Meta.
								</div>
							</div>
						</div>
					</section>

				</div>
			</article>
		</Layout>
	)
}

export default Geopop